import {
  AccessTime as AccessTimeIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  Repeat as RepeatIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  AutomatedMessage,
  CreateAutomatedMessageRequest,
  dayOfWeekLabels
} from '../services/automatedMessageApi';
import { Client, clientApi } from '../services/clientApi';
import { colors } from '../theme';
import CancelButton from './CancelButton';

interface AutomatedMessageModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: CreateAutomatedMessageRequest) => Promise<void>;
  editingRule?: AutomatedMessage | null;
  preSelectedClientId?: string;
  preSelectedClientName?: string;
}

const triggerTypes = [
  {
    value: 'interval',
    label: 'Periyodik Mesaj',
    icon: '🔄',
    description: 'Belirli aralıklarla tekrarlanan mesajlar',
  },
  {
    value: 'appointment_reminder',
    label: 'Randevu Hatırlatması',
    icon: '📅',
    description: 'Randevudan önce otomatik hatırlatma',
  },
  {
    value: 'program_start',
    label: 'Program Başlangıcı',
    icon: '🚀',
    description: 'Diyet programı başlamadan önce',
  },
  {
    value: 'program_end',
    label: 'Program Bitişi',
    icon: '🏁',
    description: 'Diyet programı bitmeden önce',
  },
  {
    value: 'no_weight_log',
    label: 'Kilo Kaydı Hatırlatması',
    icon: '⚖️',
    description: 'Kilo kaydı yapılmadığında',
  },
  {
    value: 'no_meal_log',
    label: 'Öğün Kaydı Hatırlatması',
    icon: '🍽️',
    description: 'Öğün kaydı yapılmadığında',
  },
  {
    value: 'custom_date',
    label: 'Özel Tarih',
    icon: '📆',
    description: 'Belirlenen tarihte (ör: doğum günü)',
  },
];

const messageTemplates = [
  {
    title: 'Motivasyon Mesajı',
    content:
      'Merhaba {ad}! 💪 Bu hafta hedeflerine bir adım daha yaklaşıyorsun. Harika gidiyorsun, devam et!',
  },
  {
    title: 'Randevu Hatırlatma',
    content:
      'Merhaba {ad}, yarın saat randevunuz var. Görüşmemizi dört gözle bekliyorum! 📅',
  },
  {
    title: 'Kilo Kaydı Hatırlatma',
    content:
      'Merhaba {ad}! ⚖️ Bir süredir kilo kaydı yapmadınız. İlerlemenizi takip etmek için lütfen güncel kilonuzu kaydedin.',
  },
  {
    title: 'Program Başlangıcı',
    content:
      'Merhaba {ad}! 🎉 Diyet programınız yarın başlıyor. Yeni bir başlangıç için hazır mısınız? Başarılar!',
  },
  {
    title: 'Program Bitişi',
    content:
      'Merhaba {ad}! 🏁 Programınız sona eriyor. Bu süreçte harika bir iş çıkardınız! Yeni hedeflerimiz için görüşelim.',
  },
  {
    title: 'Öğün Kaydı Hatırlatma',
    content:
      'Merhaba {ad}! 🍽️ Bugünkü öğünlerinizi kaydetmeyi unutmayın. Yemek takibi hedefinize ulaşmanızda çok önemli!',
  },
];

const AutomatedMessageModal: React.FC<AutomatedMessageModalProps> = ({
  open,
  onClose,
  onSave,
  editingRule,
  preSelectedClientId,
  preSelectedClientName,
}) => {
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);

  // Form state
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [triggerType, setTriggerType] = useState('interval');
  const [targetType, setTargetType] = useState<'all' | 'specific'>('all');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [intervalDays, setIntervalDays] = useState(7);
  const [dayOfWeek, setDayOfWeek] = useState<number | null>(null);
  const [sendHour, setSendHour] = useState(9);
  const [sendMinute, setSendMinute] = useState(0);
  const [daysBefore, setDaysBefore] = useState(1);
  const [inactiveDays, setInactiveDays] = useState(3);
  const [customDate, setCustomDate] = useState('');
  const [repeatYearly, setRepeatYearly] = useState(false);

  // Load clients
  useEffect(() => {
    const loadClients = async () => {
      setClientsLoading(true);
      try {
        const dietitianEmail = localStorage.getItem('userEmail') || '';
        const clientList = await clientApi.getAllClients(dietitianEmail);
        setClients(clientList);
      } catch (error) {
        console.error('Danışanlar yüklenirken hata:', error);
      } finally {
        setClientsLoading(false);
      }
    };

    if (open) {
      loadClients();
    }
  }, [open]);

  // Initialize form with editing data or preselected client
  useEffect(() => {
    if (editingRule) {
      setTitle(editingRule.title);
      setContent(editingRule.content);
      setTriggerType(editingRule.triggerType);
      setTargetType(editingRule.clientId ? 'specific' : 'all');
      setIntervalDays(editingRule.intervalDays || 7);
      setDayOfWeek(editingRule.dayOfWeek ?? null);
      setSendHour(editingRule.sendHour || 9);
      setSendMinute(editingRule.sendMinute ?? 0);
      setDaysBefore(editingRule.daysBefore || 1);
      setInactiveDays(editingRule.inactiveDays || 3);
      setCustomDate(
        editingRule.customDate
          ? new Date(editingRule.customDate).toISOString().split('T')[0]
          : ''
      );
      setRepeatYearly(editingRule.repeatYearly);

      if (editingRule.clientId) {
        const client = clients.find((c) => c.id === editingRule.clientId);
        setSelectedClient(client || null);
      }
    } else {
      // Reset form
      setTitle('');
      setContent('');
      setTriggerType('interval');
      setIntervalDays(7);
      setDayOfWeek(null);
      setSendHour(9);
      setSendMinute(0);
      setDaysBefore(1);
      setInactiveDays(3);
      setCustomDate('');
      setRepeatYearly(false);

      // Pre-select client if provided
      if (preSelectedClientId && preSelectedClientName) {
        setTargetType('specific');
        const client = clients.find((c) => c.id === preSelectedClientId);
        if (client) {
          setSelectedClient(client);
        }
      } else {
        setTargetType('all');
        setSelectedClient(null);
      }
    }
  }, [editingRule, open, preSelectedClientId, preSelectedClientName, clients]);

  const handleSubmit = async () => {
    if (!title.trim() || !content.trim()) {
      return;
    }

    setLoading(true);
    try {
      const request: CreateAutomatedMessageRequest = {
        title: title.trim(),
        content: content.trim(),
        triggerType,
        clientId: targetType === 'specific' ? selectedClient?.id : undefined,
        clientName:
          targetType === 'specific'
            ? `${selectedClient?.firstName} ${selectedClient?.lastName}`
            : undefined,
        sendHour,
        sendMinute,
      };

      // Add trigger-specific fields
      if (triggerType === 'interval') {
        request.intervalDays = intervalDays;
        request.dayOfWeek = dayOfWeek ?? undefined;
      } else if (
        ['appointment_reminder', 'program_start', 'program_end'].includes(
          triggerType
        )
      ) {
        request.daysBefore = daysBefore;
      } else if (['no_weight_log', 'no_meal_log'].includes(triggerType)) {
        request.inactiveDays = inactiveDays;
      } else if (triggerType === 'custom_date') {
        request.customDate = customDate;
        request.repeatYearly = repeatYearly;
      }

      await onSave(request);
      onClose();
    } catch (error) {
      console.error('Kural kaydedilirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyTemplate = (template: { title: string; content: string }) => {
    setTitle(template.title);
    setContent(template.content);
  };

  const selectedTrigger = triggerTypes.find((t) => t.value === triggerType);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          background: colors.gradients.primary,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2,
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6" fontWeight={700}>
            {editingRule ? 'Kuralı Düzenle' : 'Yeni Kural'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white',width:'fit-content' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Şablon Seçimi */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ mb: 1 }}
          >
            Hızlı Şablonlar
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {messageTemplates.map((template, index) => (
              <Chip
                key={index}
                label={template.title}
                onClick={() => applyTemplate(template)}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: colors.alpha.primary15,
                    borderColor: colors.primary.main,
                  },
                }}
                variant="outlined"
                size="small"
              />
            ))}
          </Box>
        </Box>

        {/* Kural Adı */}
        <TextField
          fullWidth
          label="Kural Adı"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ mb: 3 }}
          placeholder="Örn: Haftalık Motivasyon Mesajı"
          required
        />

        {/* Tetikleyici Türü */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Tetikleyici Türü</InputLabel>
          <Select
            value={triggerType}
            onChange={(e) => setTriggerType(e.target.value)}
            label="Tetikleyici Türü"
          >
            {triggerTypes.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                <Box display="flex" alignItems="center" gap={1}>
                  <span>{type.icon}</span>
                  <Box>
                    <Typography variant="body2">{type.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {type.description}
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Tetikleyici Bilgisi */}
        {selectedTrigger && (
          <Alert
            severity="info"
            icon={<InfoIcon />}
            sx={{ mb: 3, borderRadius: 2 }}
          >
            <Typography variant="body2">
              <strong>{selectedTrigger.icon} {selectedTrigger.label}:</strong>{' '}
              {selectedTrigger.description}
            </Typography>
          </Alert>
        )}

        {/* Tetikleyiciye Göre Ayarlar */}
        <Box sx={{ mb: 3, p: 2, bgcolor: colors.background.light, borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTimeIcon fontSize="small" /> Zamanlama Ayarları
          </Typography>

          {triggerType === 'interval' && (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                type="number"
                label="Kaç günde bir"
                value={intervalDays}
                onChange={(e) => setIntervalDays(Number(e.target.value))}
                sx={{ width: 150 }}
                inputProps={{ min: 1, max: 365 }}
                InputProps={{
                  endAdornment: <Typography variant="caption">gün</Typography>,
                }}
              />
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Gün (opsiyonel)</InputLabel>
                <Select
                  value={dayOfWeek === null ? '' : String(dayOfWeek)}
                  onChange={(e) => {
                    const val = e.target.value as string;
                    setDayOfWeek(val === '' ? null : Number(val));
                  }}
                  label="Gün (opsiyonel)"
                >
                  <MenuItem value="">Her gün</MenuItem>
                  {Object.entries(dayOfWeekLabels).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                type="number"
                label="Gönderim saati"
                value={sendHour}
                onChange={(e) => setSendHour(Number(e.target.value))}
                sx={{ width: 120 }}
                inputProps={{ min: 0, max: 23 }}
              />
              <TextField
                type="number"
                label="Dakika"
                value={sendMinute}
                onChange={(e) => setSendMinute(Number(e.target.value))}
                sx={{ width: 120 }}
                inputProps={{ min: 0, max: 59 }}
                InputProps={{
                  endAdornment: <Typography variant="caption">dk</Typography>,
                }}
              />
            </Box>
          )}

          {['appointment_reminder', 'program_start', 'program_end'].includes(
            triggerType
          ) && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                type="number"
                label={
                  triggerType === 'appointment_reminder'
                    ? 'Randevudan kaç gün önce'
                    : triggerType === 'program_start'
                    ? 'Programdan kaç gün önce'
                    : 'Programın bitmesine kaç gün kala'
                }
                value={daysBefore}
                onChange={(e) => setDaysBefore(Number(e.target.value))}
                sx={{ width: 250 }}
                inputProps={{ min: 0, max: 30 }}
                InputProps={{
                  endAdornment: <Typography variant="caption">gün</Typography>,
                }}
              />
              <TextField
                type="number"
                label="Gönderim saati"
                value={sendHour}
                onChange={(e) => setSendHour(Number(e.target.value))}
                sx={{ width: 120 }}
                inputProps={{ min: 0, max: 23 }}
              />
              <TextField
                type="number"
                label="Dakika"
                value={sendMinute}
                onChange={(e) => setSendMinute(Number(e.target.value))}
                sx={{ width: 120 }}
                inputProps={{ min: 0, max: 59 }}
                InputProps={{
                  endAdornment: <Typography variant="caption">dk</Typography>,
                }}
              />
            </Box>
          )}

          {['no_weight_log', 'no_meal_log'].includes(triggerType) && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                type="number"
                label={
                  triggerType === 'no_weight_log'
                    ? 'Kaç gün kilo kaydı yapılmadığında'
                    : 'Kaç gün öğün kaydı yapılmadığında'
                }
                value={inactiveDays}
                onChange={(e) => setInactiveDays(Number(e.target.value))}
                sx={{ width: 300 }}
                inputProps={{ min: 1, max: 30 }}
                InputProps={{
                  endAdornment: <Typography variant="caption">gün</Typography>,
                }}
              />
              <TextField
                type="number"
                label="Gönderim saati"
                value={sendHour}
                onChange={(e) => setSendHour(Number(e.target.value))}
                sx={{ width: 120 }}
                inputProps={{ min: 0, max: 23 }}
              />
              <TextField
                type="number"
                label="Dakika"
                value={sendMinute}
                onChange={(e) => setSendMinute(Number(e.target.value))}
                sx={{ width: 120 }}
                inputProps={{ min: 0, max: 59 }}
                InputProps={{
                  endAdornment: <Typography variant="caption">dk</Typography>,
                }}
              />
            </Box>
          )}

          {triggerType === 'custom_date' && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                type="date"
                label="Tarih"
                value={customDate}
                onChange={(e) => setCustomDate(e.target.value)}
                sx={{ width: 200 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type="number"
                label="Gönderim saati"
                value={sendHour}
                onChange={(e) => setSendHour(Number(e.target.value))}
                sx={{ width: 120 }}
                inputProps={{ min: 0, max: 23 }}
              />
              <TextField
                type="number"
                label="Dakika"
                value={sendMinute}
                onChange={(e) => setSendMinute(Number(e.target.value))}
                sx={{ width: 120 }}
                inputProps={{ min: 0, max: 59 }}
                InputProps={{
                  endAdornment: <Typography variant="caption">dk</Typography>,
                }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={repeatYearly}
                    onChange={(e) => setRepeatYearly(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <RepeatIcon fontSize="small" />
                    Her yıl tekrarla
                  </Box>
                }
              />
            </Box>
          )}
        </Box>

        {/* Hedef Kitle */}
        <Box sx={{ mb: 3, p: 2, bgcolor: colors.background.light, borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PeopleIcon fontSize="small" /> Hedef Kitle
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button
              variant={targetType === 'all' ? 'contained' : 'outlined'}
              onClick={() => {
                setTargetType('all');
                setSelectedClient(null);
              }}
              startIcon={<PeopleIcon />}
              sx={{
                color: targetType === 'all' ? undefined : colors.primary.main,
                borderColor: targetType === 'all' ? undefined : colors.primary.main,
                bgcolor: targetType === 'all' ? colors.primary.main : undefined,
                '&:hover': {
                  color: targetType === 'all' ? undefined : colors.primary.medium,
                  borderColor: targetType === 'all' ? undefined : colors.primary.medium,
                  bgcolor: targetType === 'all' ? colors.primary.medium : colors.alpha.primary10,
                },
              }}
            >
              Tüm Danışanlar
            </Button>
            <Button
              variant={targetType === 'specific' ? 'contained' : 'outlined'}
              onClick={() => setTargetType('specific')}
              startIcon={<PersonIcon />}
              sx={{
                color: targetType === 'specific' ? undefined : colors.primary.main,
                borderColor: targetType === 'specific' ? undefined : colors.primary.main,
                bgcolor: targetType === 'specific' ? colors.primary.main : undefined,
                '&:hover': {
                  color: targetType === 'specific' ? undefined : colors.primary.medium,
                  borderColor: targetType === 'specific' ? undefined : colors.primary.medium,
                  bgcolor: targetType === 'specific' ? colors.primary.medium : colors.alpha.primary10,
                },
              }}
            >
              Belirli Danışan
            </Button>
          </Box>

          {targetType === 'specific' && (
            <Autocomplete
              options={clients}
              loading={clientsLoading}
              value={selectedClient}
              onChange={(_, newValue) => setSelectedClient(newValue)}
              getOptionLabel={(option) =>
                `${option.firstName} ${option.lastName}`
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Danışan Seçin"
                  placeholder="İsim veya soyisim ile arayın"
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body2">
                      {option.firstName} {option.lastName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.email}
                    </Typography>
                  </Box>
                </li>
              )}
            />
          )}
        </Box>

        {/* Mesaj İçeriği */}
        <Box sx={{ mb: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="subtitle2">Mesaj İçeriği</Typography>
            <Tooltip
              title={
                <Box>
                  <Typography variant="caption" display="block">
                    Kullanılabilir değişkenler:
                  </Typography>
                  <Typography variant="caption" display="block">
                    {'{ad}'} - Danışan adı
                  </Typography>
                  <Typography variant="caption" display="block">
                    {'{soyad}'} - Danışan soyadı
                  </Typography>
                  <Typography variant="caption" display="block">
                    {'{tam_ad}'} - Tam isim
                  </Typography>
                  <Typography variant="caption" display="block">
                    {'{hedef}'} - Hedef
                  </Typography>
                  <Typography variant="caption" display="block">
                    {'{kilo}'} - Güncel kilo
                  </Typography>
                </Box>
              }
            >
              <IconButton size="small">
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Mesaj içeriğinizi yazın. {ad} gibi değişkenler kullanabilirsiniz."
            required
          />
        </Box>

        {/* Önizleme */}
        {content && (
          <Box
            sx={{
              p: 2,
              bgcolor: colors.alpha.primary05,
              borderRadius: 2,
              border: `1px solid ${colors.border.light}`,
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Önizleme (örnek danışan: Ahmet Yılmaz)
            </Typography>
            <Typography variant="body2">
              {content
                .replace('{ad}', 'Ahmet')
                .replace('{soyad}', 'Yılmaz')
                .replace('{tam_ad}', 'Ahmet Yılmaz')
                .replace('{hedef}', 'Kilo vermek')
                .replace('{kilo}', '75')}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <CancelButton onClick={onClose} />
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !title.trim() || !content.trim()}
          startIcon={<SaveIcon />}
          sx={{
            bgcolor: colors.primary.main,
            '&:hover': { bgcolor: colors.hover.primary },
          }}
        >
          {loading ? 'Kaydediliyor...' : editingRule ? 'Güncelle' : 'Oluştur'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AutomatedMessageModal;

